// hooks/useOrderAnalytics.js
import { useMemo } from "react";

const SPARKLINE_LENGTH = 7;
const ZERO_SPARKLINE = Array(SPARKLINE_LENGTH).fill(0);

function sanitizeLabel(label) {
  if (typeof label === "string" && label.trim()) {
    return label.trim();
  }
  return "Unknown";
}

function sanitizeRawValue(rawValue) {
  const numeric = Number(rawValue);
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.round(numeric);
  }
  return 0;
}

function sanitizeValueString(value, rawValue) {
  const expected = rawValue.toLocaleString("en-KE");
  if (typeof value === "string" && value.trim() === expected) {
    return value.trim();
  }
  return expected;
}

function sanitizeComparison(comparison) {
  const incomingValue = Number(comparison?.value);
  const sanitizedValue = Number.isInteger(incomingValue) && incomingValue > 0 ? incomingValue : 0;

  const directionRaw = typeof comparison?.direction === "string" ? comparison.direction.toLowerCase() : null;
  const sanitizedDirection = directionRaw === "up" || directionRaw === "down" ? directionRaw : "neutral";

  if (sanitizedValue === 0) {
    return { value: 0, direction: "neutral" };
  }

  if (sanitizedDirection === "neutral") {
    return { value: sanitizedValue, direction: "neutral" };
  }

  return {
    value: sanitizedValue,
    direction: sanitizedDirection,
  };
}

function trimTrailingDecimals(valueString) {
  return valueString.replace(/\.0+$/, "").replace(/(\.\d*?[1-9])0+$/, "$1");
}

function formatCompactValue(rawValue) {
  if (!Number.isFinite(rawValue) || rawValue === 0) {
    return "0";
  }

  const absValue = Math.abs(rawValue);
  const sign = rawValue < 0 ? "-" : "";

  if (absValue >= 1_000_000_000) {
    const base = absValue / 1_000_000_000;
    const decimals = base < 10 ? 3 : base < 100 ? 2 : 1;
    const formatted = trimTrailingDecimals(base.toFixed(decimals));
    return `${sign}${formatted} B`;
  }

  if (absValue >= 1_000_000) {
    const base = absValue / 1_000_000;
    const decimals = base < 10 ? 3 : base < 100 ? 2 : 1;
    const formatted = trimTrailingDecimals(base.toFixed(decimals));
    return `${sign}${formatted} M`;
  }

  if (absValue >= 100_000) {
    const base = absValue / 1_000;
    const formatted = trimTrailingDecimals(base.toFixed(1));
    return `${sign}${formatted} K`;
  }

  return `${sign}${rawValue.toLocaleString("en-KE")}`;
}

function sanitizeSparkline(sparkline) {
  if (!Array.isArray(sparkline) || sparkline.length === 0) {
    return { data: ZERO_SPARKLINE, hasValidValues: false };
  }

  const allNumbers = sparkline.every((point) => typeof point === "number" && Number.isFinite(point));
  if (!allNumbers) {
    return { data: ZERO_SPARKLINE, hasValidValues: false };
  }

  const trimmed = sparkline.slice(-SPARKLINE_LENGTH);
  const paddingLength = Math.max(SPARKLINE_LENGTH - trimmed.length, 0);
  const padded = Array(paddingLength).fill(0).concat(trimmed);

  return { data: padded, hasValidValues: true };
}

function sanitizeMetric(metric) {
  const safeLabel = sanitizeLabel(metric?.label);
  const safeRawValue = sanitizeRawValue(metric?.rawValue);
  const safeValue = sanitizeValueString(metric?.value, safeRawValue);
  const safeComparison = sanitizeComparison(metric?.comparison);
  const { data: safeSparkline, hasValidValues } = sanitizeSparkline(metric?.sparkline);
  const displayValue = formatCompactValue(safeRawValue);

  return {
    ...metric,
    label: safeLabel,
    rawValue: safeRawValue,
    value: safeValue,
    displayValue,
    comparison: safeComparison,
    sparkline: safeSparkline,
    hasValidSparkline: hasValidValues,
  };
}

export function useOrderAnalytics() {
  // mock backend-style analytics
  //   TODO: Will be replaced with real data from backend.
  const mockAnalytics = [
    {
      label: "Total Orders",
      rawValue: 45,
      value: "00909",
      comparison: { value: 0, direction: "" },
      sparkline: [100, ],
    },
    {
      label: "Items Ordered",
      rawValue: 2874,
      value: "2,874",
      comparison: { value: 8, direction: "up" },
      sparkline: [300, 310, 305, 320, 330, 340, 360],
    },
    {
      label: "Orders Fulfilled",
      rawValue: 1175,
      value: "1,175",
      comparison: { value: 6, direction: "up" },
      sparkline: [0, 0, 0, 0, 0, 0, 122],
    },
    {
      label: "Total Revenue",
      rawValue: 53000,
      value: "53,000",
      unit: "KES",
      comparison: { value: 14, direction: "up" },
      sparkline: [],
    },
  ];

  const trendMap = {
    "Total Orders": "up",
    "Items Ordered": "up",
    "Orders Fulfilled": "up",
    "Total Revenue": "up",
  };

  // Transform with sanitization and derived positivity flag
  const analytics = useMemo(() => {
    return mockAnalytics.map((metric) => {
      const sanitized = sanitizeMetric(metric);
      const expectedTrend = trendMap[sanitized.label];
      const direction = sanitized.comparison.direction;
      const isPositive = direction === "neutral" ? undefined : direction === expectedTrend;
      return {
        ...sanitized,
        isPositive,
      };
    });
  }, []);

  return analytics;
}